// The consent/debrief BLOCK DOCUMENT contract, web half. MIRRORS the desktop
// reader (JOLIVE, src/jolive/ui/form_blocks.py) - if the two rules diverge,
// participants see different documents per platform.
//
// A form's `content` field is ALWAYS a string:
//   - legacy: raw HTML, rendered exactly as before (one implicit text block).
//   - block document: JSON text of the shape
//     {"schema_version": 1, "blocks": [{"id", "type": "text", "html"}]}.
// Detection is parse-based: starts with "{" AND parses as JSON AND carries a
// "blocks" array. Anything else is legacy HTML. Unknown block types drop out
// at render time (forward compatibility for Phase 2 interactive blocks).

import DOMPurify from "dompurify";

export interface FormBlock {
  id?: string;
  type?: string;
  html?: string;
  [key: string]: unknown;
}

export interface Gate {
  agree_label?: string;
  agree_color?: string;
  decline_label?: string;
  decline_color?: string;
}

/** The agree/decline gate settings stored on a form's content, or null when
 * absent (the renderer then uses its per-type default labels/colors). Mirrors
 * the desktop form_blocks.parse_gate. */
export function parseGate(content: string | null | undefined): Gate | null {
  if (content && content.trimStart().startsWith("{")) {
    try {
      const doc: unknown = JSON.parse(content);
      const gate = (doc as { gate?: unknown } | null)?.gate;
      if (gate && typeof gate === "object" && !Array.isArray(gate)) {
        return gate as Gate;
      }
    } catch {
      // Not JSON after all.
    }
  }
  return null;
}

export function parseFormContent(
  content: string | null | undefined,
): FormBlock[] {
  if (!content || !content.trim()) return [];
  if (content.trimStart().startsWith("{")) {
    try {
      const doc: unknown = JSON.parse(content);
      const blocks = (doc as { blocks?: unknown } | null)?.blocks;
      if (Array.isArray(blocks)) {
        return blocks.filter(
          (b): b is FormBlock =>
            !!b && typeof b === "object" && !Array.isArray(b),
        );
      }
    } catch {
      // Not JSON after all: legacy HTML that happens to start with "{".
    }
  }
  return [{ type: "text", html: content }];
}

// Block types that render as flowing rich text. Any other type is skipped
// here (interactive blocks like signature/clause render as their own widgets).
const FLOWING_TYPES = new Set(["title", "text"]);

// Interactive (non-flowing) block types: rendered as participant inputs, and
// together they form the gate the participant must satisfy to continue.
// Mirrors the desktop form_blocks.INTERACTIVE_TYPES.
const INTERACTIVE_TYPES = new Set(["signature", "clause"]);

/** The signature/clause blocks from a parsed block list, in order. */
export function interactiveBlocks(blocks: FormBlock[]): FormBlock[] {
  return blocks.filter((b) => INTERACTIVE_TYPES.has(b.type ?? ""));
}

// The participant's answers to the interactive gate, keyed by block id:
// the typed name for a signature, the checked bool for a clause.
export type GateValues = Record<string, string | boolean>;

/** The recorded interactive answers: the typed signature (first signature
 * block's value) and the ids of the acknowledged clause blocks. Shape mirrors
 * the backend's consent/debrief recording payload. */
export function gateRecording(
  blocks: FormBlock[],
  values: GateValues,
): { signature: string; acknowledged_clauses: string[] } {
  let signature = "";
  const acknowledged_clauses: string[] = [];
  for (const b of interactiveBlocks(blocks)) {
    const id = b.id ?? "";
    if (b.type === "signature") {
      const v = values[id];
      if (!signature && typeof v === "string" && v.trim()) signature = v.trim();
    } else if (b.type === "clause") {
      if (values[id] === true) acknowledged_clauses.push(id);
    }
  }
  return { signature, acknowledged_clauses };
}

/** True once every REQUIRED signature is typed and every required clause is
 * checked. A form with no required interactive blocks is always satisfied, so
 * a plain text-only consent never blocks. Mirrors the desktop
 * ParticipantGate.is_satisfied. */
export function gateSatisfied(blocks: FormBlock[], values: GateValues): boolean {
  for (const b of interactiveBlocks(blocks)) {
    if (b.required === false) continue;
    const v = values[b.id ?? ""];
    if (b.type === "signature") {
      if (typeof v !== "string" || !v.trim()) return false;
    } else if (b.type === "clause") {
      if (v !== true) return false;
    }
  }
  return true;
}

/** The interactive gate below a consent/debrief document: the typed-name
 * signature fields and per-clause acknowledgement checkboxes. Controlled: the
 * renderer owns the values map and passes onChange. Renders nothing when the
 * form has no interactive blocks. Twin of the desktop ParticipantGate. */
export function ParticipantGate({
  blocks,
  values,
  onChange,
}: {
  blocks: FormBlock[];
  values: GateValues;
  onChange: (id: string, value: string | boolean) => void;
}) {
  const items = interactiveBlocks(blocks);
  if (!items.length) return null;
  return (
    <div className="jol-pgate">
      {items.map((b, i) => {
        const id = b.id ?? String(i);
        const required = b.required !== false;
        if (b.type === "signature") {
          const value = typeof values[id] === "string" ? (values[id] as string) : "";
          return (
            <div key={id} className="jol-pgate-sig">
              <label className="jol-pgate-label" htmlFor={`sig-${id}`}>
                {typeof b.label === "string" && b.label ? b.label : "Signature"}
                {required ? <span className="jol-pgate-req"> *</span> : null}
              </label>
              <input
                id={`sig-${id}`}
                className="jol-input jol-pgate-input"
                type="text"
                placeholder="Type your full name to sign"
                value={value}
                onChange={(e) => onChange(id, e.target.value)}
              />
            </div>
          );
        }
        // clause
        return (
          <label key={id} className="jol-pgate-clause">
            <input
              type="checkbox"
              className="jol-pgate-check"
              checked={values[id] === true}
              onChange={(e) => onChange(id, e.target.checked)}
            />
            <span
              className="jol-pgate-clause-text"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(typeof b.html === "string" ? b.html : ""),
              }}
            />
            {required ? <span className="jol-pgate-req"> *</span> : null}
          </label>
        );
      })}
    </div>
  );
}

/** The block list as sanitized HTML sections. A legacy form is exactly one
 * `.jol-content` div, byte-identical to the pre-block rendering. */
export function FormBlocksView({ blocks }: { blocks: FormBlock[] }) {
  return (
    <>
      {blocks
        .filter((b) => FLOWING_TYPES.has(b.type ?? "text"))
        .map((b, i) => (
          <div
            key={b.id ?? i}
            className="jol-content"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                typeof b.html === "string" ? b.html : "",
              ),
            }}
          />
        ))}
    </>
  );
}
